/**
 * S22 — Reports · Pre-Built Packs (`/reports/packs`).
 * One-click signed evidence packs for every framework.
 *
 * Wave 30 — Phase 3b. Migrated off `useTenantData().reportPacks` onto
 * the `/api/report-packs` REST endpoint via `useEntityList()`.
 */
import React from "react";
import { FileSignature, FileCheck2, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";

export default function ReportPacks() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    items: reportPacks,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntityList("/api/report-packs");

  if (loading && reportPacks.length === 0) {
    return <Skeleton />;
  }
  if (error) {
    return <ErrorView message={error.message} onRetry={refetch} />;
  }
  if (isCustomer && !reportPacks.length) {
    return <Empty userName={user?.name?.split(" ")[0]} />;
  }

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="report-packs"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Reports · Pre-Built Packs</p>
          <h1 className="text-display-md text-ink-primary mt-1">Evidence Packs</h1>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            One-click signed evidence packs — every pack is ALGOS-signed,
            tamper-evident and stamped with the audit-chain root at generation
            time.
          </p>
        </div>
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="report-packs-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <KpiTile
          label="Pack templates"
          value={reportPacks.length.toString()}
          delta="ready to generate"
          deltaTone="neutral"
          hint="across every framework"
          icon={FileSignature}
          tone="tralenex"
          testId="rp-kpi-templates"
        />
        <KpiTile
          label="Signed packs"
          value={reportPacks.length.toString()}
          delta="all packs"
          deltaTone="up"
          hint="tamper-evident output"
          icon={FileCheck2}
          tone="low"
          testId="rp-kpi-signed"
        />
        <KpiTile
          label="Median pages"
          value={medianPages(reportPacks).toString()}
          delta="per pack"
          deltaTone="neutral"
          hint="board / regulator ready"
          icon={Sparkles}
          tone="gold"
          testId="rp-kpi-pages"
        />
      </div>

      <div
        className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="rp-grid"
      >
        {reportPacks.map((p) => (
          <article
            key={p.id}
            className="surface-card shadow-e1 p-4 flex flex-col gap-3"
            data-testid={`rp-card-${p.id}`}
          >
            <header>
              <p className="text-meta font-mono text-ink-secondary">{p.id}</p>
              <h3 className="mt-1 text-h3 text-ink-primary leading-snug">
                {p.title}
              </h3>
              <p className="text-meta text-ink-secondary mt-0.5">{p.framework}</p>
            </header>
            <p className="text-sm text-ink-primary leading-snug">
              {p.description}
            </p>
            <dl className="grid grid-cols-3 gap-1 text-meta">
              <Field label="Pages" value={`${p.estPages}`} />
              <Field label="Owner" value={p.owner} />
              <Field label="Last used" value={p.lastUsed} />
            </dl>
            <div className="mt-auto flex gap-2">
              <button
                type="button"
                onClick={() =>
                  toast.success(`Generating ${p.title}`, {
                    description:
                      "Pack will land in /reports/library as soon as the audit-chain seal is computed.",
                  })
                }
                data-testid={`rp-generate-${p.id}`}
                className="flex-1 inline-flex items-center justify-center gap-1 h-8 px-3 rounded-md bg-tralenex-600 text-white text-meta font-semibold hover:bg-tralenex-700"
              >
                <Sparkles size={12} /> Generate
              </button>
              <button
                type="button"
                onClick={() =>
                  toast.info("Sample pack downloaded (PDF)", {
                    description: `${p.title} — sample only, no live evidence.`,
                  })
                }
                data-testid={`rp-sample-${p.id}`}
                className="inline-flex items-center justify-center gap-1 h-8 px-3 rounded-md border border-border-subtle text-meta text-ink-secondary hover:text-ink-primary"
              >
                <Download size={12} /> Sample
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-ink-secondary uppercase tracking-[0.06em] font-semibold">
        {label}
      </dt>
      <dd className="text-ink-primary mt-0.5">{value}</dd>
    </div>
  );
}

function medianPages(packs) {
  if (!packs.length) return 0;
  const s = [...packs].map((p) => p.estPages || 0).sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

function Skeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="report-packs-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-44 rounded-md surface-card animate-pulse bg-inset/20" />
        ))}
      </div>
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="report-packs-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Reports · Pre-Built Packs</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load pack templates</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="report-packs-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div
      className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden"
      data-testid="rp-customer-empty"
    >
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Reports · Pre-Built Packs</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 text-[15px]">
          Enable a framework in Compliance to unlock its evidence pack template
          here.
        </p>
      </div>
    </div>
  );
}
